import React, { FormEvent, useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import baconLogo from "../../assets/images/baconappen.png";
import { apiFetch } from "../../helpers/api";
import { setAuthorization } from "../../helpers/api_helper";
import { loginSuccess } from "../../slices/auth/login/reducer";
import { changeLayoutMode } from "../../slices/thunks";
import { LAYOUT_MODE_TYPES } from "../../Components/constants/layout";
import { guardarTemaLocal } from "../../helpers/themeStorage";
import {
  RespuestaDesafioDobleFactorDto,
  RespuestaLoginApiDto,
  RespuestaLoginDto,
} from "../../types/auth";
import { obtenerPrimerAccesoPermitido } from "../../Routes/firstAllowedRoute.utils";

const DOMINIOS_CORPORATIVOS = [
  "diesa.com.py",
  "gamaemprendimientos.com",
  "gamaemprendimientos.com.py",
  "bacon.com",
  "bacon.com.py",
  "baconburgers.com",
  "baconburgers.com.py",
  "mgpro.com",
  "mgpro.com.py",
];

type Paso = "credenciales" | "codigo";

interface ErroresFormulario {
  usuario?: string;
  contrasena?: string;
  codigo?: string;
}

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [usuario, setUsuario] = useState("");
  const [contrasena, setContrasena] = useState("");
  const [recordar, setRecordar] = useState(false);

  const [pasoActual, setPasoActual] = useState<Paso>("credenciales");
  const [codigo, setCodigo] = useState("");
  const [desafioId, setDesafioId] = useState<string | null>(null);
  const [expiraEn, setExpiraEn] = useState<string | null>(null);
  const [tiempoRestante, setTiempoRestante] = useState<number | null>(null);

  const [errores, setErrores] = useState<ErroresFormulario>({});
  const [mensajeError, setMensajeError] = useState("");
  const [mensajeInfo, setMensajeInfo] = useState("");
  const [avisoConexion, setAvisoConexion] = useState("");
  const [avisoDominio, setAvisoDominio] = useState("");
  const [avisoCredenciales, setAvisoCredenciales] = useState("");
  const [mensajeSistema, setMensajeSistema] = useState("");

  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    document.title = "Inicio de sesion | BaconApp - Panel de administracion";
  }, []);

  useEffect(() => {
    if (searchParams.get("recuperado") === "1") {
      setMensajeSistema("Tu contraseña fue actualizada. Inicia sesión con tus nuevos datos.");
      const next = new URLSearchParams(searchParams);
      next.delete("recuperado");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const reiniciarFlujo = useCallback(() => {
    setPasoActual("credenciales");
    setCodigo("");
    setDesafioId(null);
    setExpiraEn(null);
    setErrores({});
    setMensajeInfo("");
    setTiempoRestante(null);
  }, []);

  const persistirSesion = useCallback(
    (payload: RespuestaLoginDto) => {
      const authPayload = {
        token: payload.accessToken,
        sessionId: payload.sessionId,
        usuario: payload.usuario,
        configuracion: payload.configuracion,
      };
      sessionStorage.setItem("authUser", JSON.stringify(authPayload));
      localStorage.setItem("token", payload.accessToken);
      setAuthorization(payload.accessToken);
      dispatch(loginSuccess(authPayload));
      const temaPersistido =
        payload.configuracion?.tema === "light"
          ? LAYOUT_MODE_TYPES.LIGHTMODE
          : LAYOUT_MODE_TYPES.DARKMODE;
      guardarTemaLocal(temaPersistido === LAYOUT_MODE_TYPES.LIGHTMODE ? "light" : "dark");
      dispatch(changeLayoutMode(temaPersistido));
      const destino = obtenerPrimerAccesoPermitido() ?? "/inicio";
      navigate(destino);
    },
    [dispatch, navigate]
  );

  const validarCredenciales = () => {
    const nuevoErrores: ErroresFormulario = {};
    const correo = usuario.trim().toLowerCase();
    if (!correo) {
      nuevoErrores.usuario = "El correo corporativo es obligatorio.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo)) {
      nuevoErrores.usuario = "Ingresa un correo válido.";
    } else {
      const dominio = correo.split("@").pop() ?? "";
      if (!DOMINIOS_CORPORATIVOS.includes(dominio)) {
        nuevoErrores.usuario = "El correo debe ser corporativo.";
        setAvisoDominio(
          "Solo se permiten correos corporativos (gamaemprendimientos, bacon, baconburgers, mgpro)."
        );
      }
    }

    if (!contrasena.trim()) {
      nuevoErrores.contrasena = "La contraseña es obligatoria.";
    } else if (contrasena.trim().length < 4) {
      nuevoErrores.contrasena = "La contraseña debe tener al menos 4 caracteres.";
    }

    setErrores(nuevoErrores);
    if (nuevoErrores.usuario && nuevoErrores.usuario.includes("corporativo")) {
      setAvisoDominio(
        "Solo se permiten correos corporativos (gamaemprendimientos, bacon, baconburgers, mgpro)."
      );
    }

    return Object.keys(nuevoErrores).length === 0;
  };

  const validarCodigo = () => {
    const codigoLimpio = codigo.trim();
    if (!codigoLimpio) {
      setErrores({ codigo: "Ingresa el código enviado a tu correo." });
      return false;
    }
    if (!/^[0-9]{6}$/.test(codigoLimpio)) {
      setErrores({ codigo: "El código debe tener 6 dígitos numéricos." });
      return false;
    }
    setErrores({});
    return true;
  };

  const manejarEnvioCredenciales = async (evento: FormEvent<HTMLFormElement>) => {
    evento.preventDefault();
    setMensajeError("");
    setMensajeInfo("");
    setAvisoConexion("");
    setAvisoCredenciales("");

    if (!validarCredenciales()) {
      return;
    }

    const correo = usuario.trim().toLowerCase();

    setCargando(true);
    try {
      const respuesta = await apiFetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ usuario: correo, contrasena }),
      });

      if (!respuesta.ok) {
        if (respuesta.status === 401) {
          setAvisoCredenciales("Usuario o contraseña incorrectos.");
        } else {
          setMensajeError("No se pudo iniciar sesión. Inténtalo nuevamente.");
        }
        return;
      }

      const datos = (await respuesta.json()) as RespuestaLoginApiDto;
      if ((datos as RespuestaDesafioDobleFactorDto).requiereDobleFactor) {
        const reto = datos as RespuestaDesafioDobleFactorDto;
        setPasoActual("codigo");
        setCodigo("");
        setDesafioId(reto.desafioId);
        setExpiraEn(reto.expiraEn);
        setMensajeInfo(
          reto.mensaje ||
            `Código enviado a ${correo}. Revisa tu correo corporativo para completarlo.`
        );
        setErrores({});
        return;
      }

      persistirSesion(datos as RespuestaLoginDto);
    } catch (error) {
      console.error(error);
      setAvisoConexion("No se pudo conectar con el servidor. Verifica que el backend esté en ejecución.");
    } finally {
      setCargando(false);
    }
  };

  const manejarVerificacionCodigo = async (evento: FormEvent<HTMLFormElement>) => {
    evento.preventDefault();
    setMensajeError("");
    if (!validarCodigo()) {
      return;
    }
    if (!desafioId) {
      setMensajeError("El desafío de verificación expiró. Vuelve a iniciar sesión.");
      reiniciarFlujo();
      return;
    }

    setCargando(true);
    try {
      const respuesta = await apiFetch("/api/auth/verificar-doble-factor", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ desafioId, codigo: codigo.trim() }),
      });

      if (!respuesta.ok) {
        if (respuesta.status === 401) {
          setMensajeError("El código ingresado no es válido o expiró.");
        } else {
          setMensajeError("No se pudo validar el código. Inténtalo nuevamente.");
        }
        return;
      }

      const datos = (await respuesta.json()) as RespuestaLoginDto;
      persistirSesion(datos);
    } catch (error) {
      console.error(error);
      setAvisoConexion("No se pudo conectar con el servidor. Verifica que el backend esté en ejecución.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (!expiraEn || pasoActual !== "codigo") {
      setTiempoRestante(null);
      return undefined;
    }

    const actualizar = () => {
      const diff = new Date(expiraEn).getTime() - Date.now();
      setTiempoRestante(diff > 0 ? Math.ceil(diff / 1000) : 0);
    };

    actualizar();
    const intervalo = window.setInterval(actualizar, 1000);
    return () => window.clearInterval(intervalo);
  }, [expiraEn, pasoActual]);

  const formatearTiempo = (segundos: number | null) => {
    if (segundos === null) return "";
    const minutos = Math.floor(segundos / 60);
    const resto = segundos % 60;
    return `${minutos.toString().padStart(2, "0")}:${resto.toString().padStart(2, "0")}`;
  };

  const renderAlert = (texto: string, color: string, onClose: () => void) => (
    <Alert color={color} toggle={onClose} className="mb-3">
      {texto}
    </Alert>
  );

  const renderCredencialesForm = () => (
    <Form onSubmit={manejarEnvioCredenciales}>
      <FormGroup className="mb-3">
        <Label htmlFor="login-usuario" className="form-label">
          Correo corporativo
        </Label>
        <Input
          id="login-usuario"
          type="email"
          placeholder="tucorreo@baconburgers.com.py"
          value={usuario}
          onChange={(e) => setUsuario(e.target.value)}
          invalid={Boolean(errores.usuario)}
        />
        {errores.usuario && <div className="invalid-feedback d-block">{errores.usuario}</div>}
      </FormGroup>
      <FormGroup className="mb-3">
        <div className="float-end">
          <Link to="/forgot-password" className="text-muted">
            Olvidaste tu contraseña?
          </Link>
        </div>
        <Label className="form-label" htmlFor="login-contrasena">
          Contraseña
        </Label>
        <Input
          id="login-contrasena"
          type="password"
          placeholder="********"
          value={contrasena}
          onChange={(e) => setContrasena(e.target.value)}
          invalid={Boolean(errores.contrasena)}
        />
        {errores.contrasena && <div className="invalid-feedback d-block">{errores.contrasena}</div>}
      </FormGroup>
      <div className="form-check">
        <Input
          className="form-check-input"
          id="login-recordar"
          type="checkbox"
          checked={recordar}
          onChange={(e) => setRecordar(e.target.checked)}
        />
        <Label className="form-check-label" htmlFor="login-recordar">
          Recordarme
        </Label>
      </div>
      <div className="mt-4">
        <Button color="success" className="btn btn-success w-100" type="submit" disabled={cargando}>
          {cargando ? (
            <>
              <Spinner size="sm" className="me-2" /> Validando...
            </>
          ) : (
            "Iniciar sesión"
          )}
        </Button>
      </div>
    </Form>
  );

  const renderCodigoForm = () => (
    <Form onSubmit={manejarVerificacionCodigo}>
      <p className="text-muted text-center">
        Ingresa el código que enviamos a tu correo corporativo para completar el acceso.
      </p>
      {expiraEn && (
        <p className="text-center mb-3">
          Expira en <strong>{formatearTiempo(tiempoRestante)}</strong>
        </p>
      )}
      <FormGroup className="mb-3">
        <Label htmlFor="login-codigo" className="form-label">
          Código de verificación
        </Label>
        <Input
          id="login-codigo"
          type="text"
          maxLength={6}
          placeholder="000000"
          value={codigo}
          onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ""))}
          invalid={Boolean(errores.codigo)}
        />
        {errores.codigo && <div className="invalid-feedback d-block">{errores.codigo}</div>}
      </FormGroup>
      <div className="d-flex gap-2">
        <Button color="light" type="button" className="flex-fill" onClick={reiniciarFlujo} disabled={cargando}>
          Cancelar
        </Button>
        <Button color="success" type="submit" className="flex-fill" disabled={cargando}>
          {cargando ? (
            <>
              <Spinner size="sm" className="me-2" /> Verificando...
            </>
          ) : (
            "Validar código"
          )}
        </Button>
      </div>
    </Form>
  );

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content mt-lg-5">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4 text-white-50 auth-login-brand">
                  <div>
                    <Link to="/" className="d-inline-block auth-logo">
                      <img src={baconLogo} alt="BaconApp logo" className="logo-white-filter" height="32" />
                    </Link>
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={5} lg={4} xl={3} className="auth-login-col">
                <Card className="mt-4 auth-login-card">
                  <CardBody className="px-5 py-5">
                    <div className="text-center mt-2">
                      <h5 className="text-primary">
                        {pasoActual === "credenciales" ? "Bienvenido de nuevo" : "Verifica tu acceso"}
                      </h5>
                      <p className="text-muted">
                        {pasoActual === "credenciales"
                          ? "Inicia sesión para continuar en BaconApp."
                          : "Ingresa el código de seguridad que enviamos a tu correo corporativo."}
                      </p>
                    </div>
                    <div className="p-2 mt-4">
                      {mensajeSistema && renderAlert(mensajeSistema, "info", () => setMensajeSistema(""))}
                      {mensajeInfo && renderAlert(mensajeInfo, "info", () => setMensajeInfo(""))}
                      {mensajeError && renderAlert(mensajeError, "danger", () => setMensajeError(""))}
                      {avisoDominio && renderAlert(avisoDominio, "info", () => setAvisoDominio(""))}
                      {avisoConexion && renderAlert(avisoConexion, "danger", () => setAvisoConexion(""))}
                      {avisoCredenciales &&
                        renderAlert(avisoCredenciales, "warning", () => setAvisoCredenciales(""))}

                      {pasoActual === "credenciales" ? renderCredencialesForm() : renderCodigoForm()}
                    </div>
                    {pasoActual === "codigo" && (
                      <p className="mt-3 text-center text-muted">
                        ¿No recibiste el código?{" "}
                        <button
                          type="button"
                          className="btn btn-link p-0 align-baseline"
                          onClick={reiniciarFlujo}
                        >
                          Volver a intentar
                        </button>
                      </p>
                    )}
                  </CardBody>
                </Card>

                <div className="mt-4 text-center">
                  <p className="mb-0">
                    ¿Necesitas ayuda?{" "}
                    <Link to="/forgot-password" className="fw-semibold text-primary text-decoration-underline">
                      Recuperar contraseña
                    </Link>
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default Login;
